import { useCallback, useMemo, useState, useEffect } from "react"
import { createPortal } from "react-dom"
import {
  ReactFlow,
  Background,
  MiniMap,
  Panel,
  useNodesState,
  useEdgesState,
  ReactFlowProvider,
} from "@xyflow/react"
import "@xyflow/react/dist/style.css"
import "../flow-styles.css"

import ErrorBoundary from "../ErrorBoundary"
import { nodeTypes } from "../nodes"
import { edgeTypes } from "../edges"
import SourceFilter, { useFilters } from "../SourceFilter"
import TeamFilter, { useTeamFilters } from "../TeamFilter"
import LensSelector from "../LensSelector"
import NodeEditPanel from "../NodeEditPanel"
import EdgeEditPanel from "../EdgeEditPanel"
import { NodeProvider } from "../NodeContext"

import CustomControls from "./CustomControls"
import { miniMapNodeColor } from "./constants"
import useAutoLayout from "./hooks/useAutoLayout"
import useNodeOperations from "./hooks/useNodeOperations"
import useEdgeOperations from "./hooks/useEdgeOperations"
import useFlowPersistence from "./hooks/useFlowPersistence"
import useStimulusEvents from "./hooks/useStimulusEvents"
import useKeyboardShortcuts from "./hooks/useKeyboardShortcuts"
import useFilteredNodes from "./hooks/useFilteredNodes"
import useGlossaryTerms from "./hooks/useGlossaryTerms"
import useLensState from "./hooks/useLensState"
import useRelayout from "./hooks/useRelayout"

function FlowCanvasInner({ flowSlug, initialNodes, initialEdges, initialViewport, isGenerated }) {
  // --- State ---
  const [filters, setFilters] = useFilters()
  const { activeTeams, toggleTeam, showAll, hideAll, allTeams } = useTeamFilters()
  const [nodes, setNodes, onNodesChange] = useNodesState(initialNodes || [])
  const [edges, setEdges, onEdgesChange] = useEdgesState(initialEdges || [])
  const [selectedNode, setSelectedNode] = useState(null)
  const [selectedEdge, setSelectedEdge] = useState(null)
  const [activeLens, setActiveLens] = useLensState()
  const [panelContainer, setPanelContainer] = useState(null)

  // --- Panel container for portal ---
  useEffect(() => {
    const container = document.getElementById("node-edit-panel")
    if (container) {
      setPanelContainer(container)
    }
  }, [])

  // --- Hooks ---
  const glossaryTerms = useGlossaryTerms(activeLens)
  useKeyboardShortcuts(setActiveLens)
  useRelayout()

  const { handleAutoLayout } = useAutoLayout(nodes, edges, setNodes, { isGenerated })

  const {
    handleAddNode,
    handleNodeUpdate,
    handleNodeDelete,
    handleLabelChange,
    handleNodeDragStart,
    handleNodeDragStop,
    handleNodeClick,
    handleNodeDoubleClick,
    handlePanelClose,
  } = useNodeOperations(flowSlug, nodes, setNodes, setEdges, selectedNode, setSelectedNode, setSelectedEdge)

  const {
    onConnect,
    handleEdgeClick,
    handleEdgeUpdate,
    handleEdgeDelete,
    handleEdgePanelClose,
  } = useEdgeOperations(setEdges, setSelectedNode, selectedEdge, setSelectedEdge)

  const { handleSave, handleExport } = useFlowPersistence(flowSlug, nodes, edges)

  useStimulusEvents(handleAddNode, handleSave, handleExport)

  // --- Team filter toggle ---
  const handleTeamShowAll = useCallback(() => {
    if (activeTeams.length === allTeams.length) {
      hideAll()
    } else {
      showAll()
    }
  }, [activeTeams.length, allTeams.length, showAll, hideAll])

  // --- Filtering ---
  const { filteredNodes, filteredEdges, defaultEdgeOptions } = useFilteredNodes(nodes, edges, {
    filters,
    activeTeams,
    allTeams,
    activeLens,
    glossaryTerms,
    selectedEdge,
  })

  const defaultViewport = useMemo(
    () => initialViewport || { x: 0, y: 0, zoom: 1 },
    [initialViewport]
  )

  // --- Render ---
  return (
    <div className="react-flow-wrapper">
      <NodeProvider onLabelChange={handleLabelChange}>
      <ReactFlow
        nodes={filteredNodes}
        edges={filteredEdges}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        onConnect={onConnect}
        onNodeDragStart={handleNodeDragStart}
        onNodeDragStop={handleNodeDragStop}
        onNodeClick={handleNodeClick}
        onNodeDoubleClick={handleNodeDoubleClick}
        onEdgeClick={handleEdgeClick}
        nodeTypes={nodeTypes}
        edgeTypes={edgeTypes}
        defaultEdgeOptions={defaultEdgeOptions}
        defaultViewport={defaultViewport}
        fitView
        fitViewOptions={{ padding: 0.2 }}
        minZoom={0.1}
        maxZoom={2}
        attributionPosition="bottom-left"
        panOnDrag
        zoomOnScroll
        zoomOnPinch
        zoomOnDoubleClick={false}
        preventScrolling
      >
        <Background color="#aaa" gap={16} />
        <CustomControls onAutoLayout={handleAutoLayout} />
        <Panel position="top-right">
          <div className="filter-panels">
            <LensSelector activeLens={activeLens} onChange={setActiveLens} />
            <TeamFilter
              activeTeams={activeTeams}
              onToggle={toggleTeam}
              onShowAll={handleTeamShowAll}
            />
            <SourceFilter filters={filters} onChange={setFilters} />
          </div>
        </Panel>
        <MiniMap
          nodeStrokeColor="#666"
          nodeColor={miniMapNodeColor}
          nodeBorderRadius={4}
          pannable
          zoomable
        />
      </ReactFlow>
      </NodeProvider>
      {panelContainer &&
        createPortal(
          selectedEdge ? (
            <EdgeEditPanel
              edge={selectedEdge}
              onUpdate={handleEdgeUpdate}
              onDelete={handleEdgeDelete}
              onClose={handleEdgePanelClose}
            />
          ) : (
            <NodeEditPanel
              node={selectedNode}
              flowSlug={flowSlug}
              onUpdate={handleNodeUpdate}
              onDelete={handleNodeDelete}
              onClose={handlePanelClose}
            />
          ),
          panelContainer
        )}
    </div>
  )
}

export default function FlowCanvas(props) {
  return (
    <ErrorBoundary>
      <ReactFlowProvider>
        <FlowCanvasInner {...props} />
      </ReactFlowProvider>
    </ErrorBoundary>
  )
}
